
import { defineComponent, onMounted } from "vue";
import { setCurrentPageTitle } from "@/core/helpers/breadcrumb";
import ApiService from "@/core/services/ApiService";
import { ElNotification } from "element-plus";
import Swal from "sweetalert2/dist/sweetalert2.min.js";
import moment from "moment";
import store from "@/store";

interface RequestRow {
  id: number;
  refType: string;
  refId: string;
  requestType: number; // 1=UPDATE, 2=DELETE
  customerId: string;
  identityNo: string;
  customerName: string;
  keyName: string;
  keyValue: string;
  fieldName: string;
  oldValue: string;
  newValue: string;
  oldValueString: string;
  newValueString: string;
  requestReason: string;
  status: string;
  statusText: string;
  createdBy: string;
  createdDate: string;
  checkedBy: string;
  checkedDate: string;
  approvedBy: string;
  approvedDate: string;
  pcrMailStatus: string;
  pcrMailStatusText: string;
  pcrMailSentDate: string;
  pcrMailLastAttemptDate: string;
  pcrMailLastError: string;
  canResendPcrMail: boolean;
  searchfield: string;
}

export default defineComponent({
  name: "RequestEdit",
  setup() {
    onMounted(() => {
      setCurrentPageTitle("Хүсэлт");
    });
  },
  mounted() {
    if (this.$isMbUser) this.getRequests();
  },
  data() {
    return {
      page: 1,
      pageSize: 10,
      loadingtable: false,
      customerSearch: "",
      filterRequestType: null as null | number, // 1=UPDATE, 2=DELETE
      filterStatus: null as null | string, // PENDING, CHECKED, etc.
      filterCreatedBy: null as null | string,
      filterKeyName: null as null | string,
      filterFieldName: null as null | string,
      tableData: [] as RequestRow[],
      showRequestDetailModal: false,
      selectedRequest: null as null | RequestRow,
      reminding: false,
      withdrawing: false,
      resendingPcrMailId: null as null | number,

      // === Add Request Modal ===
      showAddModal: false,
      loadingMeta: false,
      submitting: false,
      metaError: "",
      addForm: {
        actionType: "UPDATE" as "UPDATE" | "DELETE",
        isCitizen: 1 as 0 | 1,
        customerId: "",
        identityNo: "",
        customerName: "",
        refId: "",
        modules: [] as any[], // ⬅️ backend-ийн response.modules энд хадгална
        selectedPath: [] as string[], // ⬅️ [table, keyValue, field]

        fields: [] as Array<{ field: string; label: string; oldValue: string }>,
        selectedField: "",
        oldValue: "",
        newValue: "",
        reason: ""
      }
    };
  },
  computed: {
    roleNames(): string[] {
      return store.getters.getRoleNames || [];
    },
    isAdmin(): boolean {
      return this.roleNames.includes("ROLE_ADMIN");
    },
    canReviseRequests(): boolean {
      return this.isAdmin || this.roleNames.includes("ROLE_REQUEST_REVISE");
    },
    canApproveRequests(): boolean {
      return this.isAdmin || this.roleNames.includes("ROLE_REQUEST_APPROVE");
    },
    currentUsername(): string {
      return (
        store.getters.username || store.getters.currentUser?.username || ""
      );
    },
    creatorOptions(): string[] {
      return Array.from(
        new Set(
          this.tableData
            .map(row => row.createdBy)
            .filter((value): value is string => !!value)
        )
      ).sort();
    },
    keyNameOptions(): string[] {
      return Array.from(
        new Set(
          this.tableData
            .map(row => row.keyName)
            .filter((value): value is string => !!value)
        )
      ).sort();
    },
    fieldNameOptions(): string[] {
      return Array.from(
        new Set(
          this.tableData
            .map(row => row.fieldName)
            .filter((value): value is string => !!value)
        )
      ).sort();
    },
    requestDetailRows(): Array<{ label: string; value: string }> {
      const row = this.selectedRequest;
      if (!row) return [];
      return [
        {
          label: "Request type",
          value: row.requestType === 2 ? "DELETE" : "UPDATE"
        },
        { label: "Field name", value: row.fieldName },
        { label: "Old value", value: row.oldValueString || row.oldValue },
        { label: "New value", value: row.newValueString || row.newValue },
        { label: "Customer ID", value: row.customerId },
        { label: "Customer name", value: row.customerName },
        { label: "Ref_type", value: row.refType },
        { label: "Ref_id", value: row.refId },
        { label: "Үүсгэсэн", value: row.createdBy },
        { label: "Үүсгэсэн огноо", value: row.createdDate },
        { label: "Төлөв", value: row.statusText || row.status },
        {
          label: "ЗМҮҮЭ мейл",
          value: this.pcrMailStatusLabel(row)
        },
        {
          label: "ЗМҮҮЭ мейл илгээсэн огноо",
          value: row.pcrMailSentDate || ""
        },
        {
          label: "ЗМҮҮЭ мейл алдаа",
          value: row.pcrMailLastError || ""
        },
        { label: "Тайлбар", value: row.requestReason }
      ];
    },
    filteredRows(): RequestRow[] {
      return this.tableData.filter(row => {
        // Search filter
        const matchSearch =
          !this.customerSearch ||
          row.searchfield?.includes(this.customerSearch.toLowerCase());

        // Request type filter
        const matchType =
          !this.filterRequestType ||
          (this.filterRequestType === 1 && row.requestType === 1) ||
          (this.filterRequestType === 2 && row.requestType === 2);

        // Status filter
        const matchStatus =
          !this.filterStatus || row.status === this.filterStatus;
        const matchCreatedBy =
          !this.filterCreatedBy || row.createdBy === this.filterCreatedBy;
        const matchKeyName =
          !this.filterKeyName || row.keyName === this.filterKeyName;
        const matchFieldName =
          !this.filterFieldName || row.fieldName === this.filterFieldName;

        return (
          matchSearch &&
          matchType &&
          matchStatus &&
          matchCreatedBy &&
          matchKeyName &&
          matchFieldName
        );
      });
    },
    filteredRecordCount(): number {
      return this.filteredRows.length;
    },
    filteredTableData(): RequestRow[] {
      return this.filteredRows.slice(
        this.pageSize * this.page - this.pageSize,
        this.pageSize * this.page
      );
    },
    addRules(): Record<string, any> {
      return {
        actionType: [
          { required: true, message: "Үйлдэл сонгоно уу", trigger: "change" }
        ],
        isCitizen: [
          {
            required: true,
            message: "Харилцагчийн төрлийг сонгоно уу",
            trigger: "change"
          }
        ],
        customerId: [
          {
            required: true,
            message: "Харилцагчийн ID оруулна уу",
            trigger: "blur"
          }
        ],
        selectedPath: [
          {
            validator: (_: any, v: string[], cb: any) => {
              const needLen = this.addForm.actionType === "DELETE" ? 2 : 3;
              if (!v || v.length !== needLen) {
                cb(new Error("Талбар сонгоно уу"));
              } else cb();
            },
            trigger: "change"
          }
        ],
        newValue:
          this.addForm.actionType === "UPDATE"
            ? [
                {
                  required: true,
                  message: "Шинэ утга оруулна уу",
                  trigger: "blur"
                }
              ]
            : [],
        reason: [
          { required: true, message: "Шалтгаан оруулна уу", trigger: "blur" }
        ]
      };
    },

    // Cascader-д өгөх модуль → мөр → талбар бүтэц
    cascaderOptions(): any[] {
      const DELETE_TABLES = new Set([
        "T_CUSTOMER",
        "T_LOAN",
        "T_LOANLINE",
        "T_COLLATERAL",
        "T_COLLATERAL_LINE"
      ]);

      // Only show allowed modules when DELETE
      const modules = (this.addForm.modules || []).filter((m: any) =>
        this.addForm.actionType === "DELETE" ? DELETE_TABLES.has(m.table) : true
      );

      return modules.map((m: any) => {
        const rows = (m.rows || []).map((r: any) => {
          if (this.addForm.actionType === "DELETE") {
            // 2-level cascader: just table -> row (no fields)
            return {
              value: r.keyValue, // row PK
              label: this.formatLoanRowOptionLabel(m.table, r)
              // NOTE: no children here in DELETE mode
            };
          } else {
            // UPDATE mode: table -> row -> field (your current logic)
            return {
              value: r.keyValue,
              label: this.formatLoanRowOptionLabel(m.table, r),
              children: (r.fields || []).map((f: any) => ({
                value: f.field,
                label: f.label,
                props: {
                  type: f.type,
                  options: f.options || [],
                  table: f.table,
                  tableLabel: m.label
                }
              }))
            };
          }
        });

        return {
          value: m.table,
          label: m.label,
          children: rows
        };
      });
    },

    // Сонгосон талбарын дэлгэрэнгүй (UI tag-ууд, oldValue)
    selectedFieldMeta(): null | {
      table: string;
      tableLabel: string;
      keyValue: string;
      keyLabel: string;
      field?: string; // optional in DELETE
      label?: string; // optional in DELETE
      oldValue?: string;
      options?: any[];
      type?: string;
    } {
      const p = this.addForm.selectedPath;

      // DELETE: table -> row (length 2)
      if (this.addForm.actionType === "DELETE") {
        if (!p || p.length !== 2) return null;
        const [tbl, keyVal] = p;

        for (const m of this.addForm.modules || []) {
          if (m.table !== tbl) continue;
          const r = (m.rows || []).find(
            (x: any) => String(x.keyValue) === String(keyVal)
          );
          if (!r) return null;

          return {
            table: tbl,
            tableLabel: m.label,
            keyValue: r.keyValue,
            keyLabel: this.formatLoanRowLabel(tbl, r)
            // no field/oldValue/newValue here
          };
        }
        return null;
      }

      // UPDATE: table -> row -> field (length 3) — your current logic
      if (!p || p.length !== 3) return null;
      const [tbl, keyVal, col] = p;
      for (const m of this.addForm.modules || []) {
        if (m.table !== tbl) continue;
        for (const r of m.rows || []) {
          if (String(r.keyValue) !== String(keyVal)) continue;
          const f = (r.fields || []).find((x: any) => x.field === col);
          if (!f) return null;
          return {
            table: tbl,
            tableLabel: m.label,
            keyValue: r.keyValue,
            keyLabel: this.formatLoanRowLabel(tbl, r),
            field: f.field,
            label: f.label,
            oldValue: f.oldValue,
            options: f.options || [],
            type: f.type
          };
        }
      }
      return null;
    },
    resolvedOldValue(): string {
      const meta = this.selectedFieldMeta;
      const val = this.addForm.oldValue;
      if (!meta || !meta.options?.length) return val || "";
      const found = meta.options.find(o => String(o.value) === String(val));
      return found ? String(found.label) : String(val || "");
    }
  },
  watch: {
    customerSearch() {
      this.resetPagination();
    },
    filterRequestType() {
      this.resetPagination();
    },
    filterStatus() {
      this.resetPagination();
    },
    filterCreatedBy() {
      this.resetPagination();
    },
    filterKeyName() {
      this.resetPagination();
    },
    filterFieldName() {
      this.resetPagination();
    }
  },
  methods: {
    pcrMailTagType(status: string): string {
      switch (status) {
        case "SENT":
          return "success";
        case "PENDING":
          return "warning";
        case "FAILED":
          return "danger";
        case "SKIPPED":
          return "info";
        default:
          return "danger";
      }
    },
    pcrMailStatusLabel(row: RequestRow): string {
      if (row.pcrMailStatusText) return row.pcrMailStatusText;
      if (row.pcrMailStatus === "SENT") return "ЗМҮҮЭ мейл илгээгдсэн";
      if (row.pcrMailStatus === "PENDING") return "ЗМҮҮЭ мейл илгээгдэж байна";
      if (row.pcrMailStatus === "FAILED") return "ЗМҮҮЭ мейл илгээгдээгүй";
      if (row.pcrMailStatus === "SKIPPED") return "ЗМҮҮЭ мейл илгээхгүй";
      return "ЗМҮҮЭ мейл илгээгдсэн эсэх тодорхойгүй";
    },
    canResendPcrMail(row: RequestRow): boolean {
      return (
        row.status === "APPROVED" &&
        row.pcrMailStatus !== "SENT" &&
        row.pcrMailStatus !== "PENDING" &&
        row.canResendPcrMail !== false
      );
    },
    async resendPcrMail(row: RequestRow) {
      const { isConfirmed } = await Swal.fire({
        title: "ЗМҮҮЭ мейлийг дахин илгээх үү?",
        text: `${row.id} дугаартай хүсэлт`,
        showCancelButton: true,
        confirmButtonText: "Тийм",
        cancelButtonText: "Үгүй"
      });
      if (!isConfirmed) return;

      this.resendingPcrMailId = row.id;
      try {
        const resp = await ApiService.put("updaterequest", {
          params: { type: "resendPcrMail", id: row.id }
        });
        const rs = resp.data;
        if (rs.code !== 1) {
          ElNotification({
            title: rs.code === 2 ? "Анхааруулга" : "Алдаа",
            message: rs.warning || rs.error || "Мейл дахин илгээж чадсангүй.",
            type: rs.code === 2 ? "warning" : "error"
          });
          return;
        }

        ElNotification({
          title: "Амжилттай",
          message: rs.response || "Мейл дахин илгээхээр дараалалд орлоо.",
          type: "success"
        });
        await this.getRequests();
      } catch (e) {
        ElNotification({
          title: "Алдаа",
          message: "Мейл дахин илгээхэд алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.resendingPcrMailId = null;
      }
    },
    rowFirstValue(row: any, keys: string[]): string {
      for (const key of keys) {
        const value = row?.[key];
        if (
          value !== null &&
          value !== undefined &&
          String(value).trim() !== ""
        ) {
          return String(value);
        }
      }
      return "";
    },
    isLoanTable(table: string): boolean {
      return ["T_LOAN", "T_LOANLINE"].includes(
        String(table || "").toUpperCase()
      );
    },
    getLoanRowDisplay(table: string, row: any): Record<string, string> {
      const contractNo = this.rowFirstValue(row, [
        "contractNo",
        "loanContractNo",
        "loanlineContractNo",
        "loanLineContractNo",
        "LOAN_CONTRACT_NO",
        "LOANLINE_CONTRACT_NO"
      ]);
      const startedDate = this.rowFirstValue(row, ["starteddate"]);
      const amount = this.rowFirstValue(row, ["amountLcy"]);
      const paidDate = this.rowFirstValue(row, ["paiddate"]);
      const expDate = this.rowFirstValue(row, ["expdate"]);
      const loanType = this.rowFirstValue(row, [
        "loanTypeName",
        "loanlineTypeName",
        "loanType",
        "loanlineType"
      ]);

      return {
        contractNo: contractNo || row.keyLabel || row.keyValue || "-",
        startedDate,
        amount,
        paidDate,
        expDate,
        loanType
      };
    },
    formatLoanRowOptionLabel(table: string, row: any): string {
      if (!this.isLoanTable(table)) return row.keyLabel || row.keyValue;

      const meta = this.getLoanRowDisplay(table, row);
      const parts = [
        `Гэрээ: ${meta.contractNo}`,
        `Төрөл: ${meta.loanType || "-"}`,
        `Дүн: ${meta.amount ? this.formatCurrency(meta.amount) : "-"}`
      ];

      return parts.join(" | ");
    },
    formatLoanRowLabel(table: string, row: any): string {
      if (!this.isLoanTable(table)) return row.keyLabel || row.keyValue;

      const meta = this.getLoanRowDisplay(table, row);
      const parts = [
        `Гэрээ: ${meta.contractNo}`,
        `Олгосон огноо: ${
          meta.startedDate ? moment(meta.startedDate).format("YYYY-MM-DD") : "-"
        }`,
        `Төрөл: ${meta.loanType || "-"}`,
        `Дүн: ${meta.amount ? this.formatCurrency(meta.amount) : "-"}`,
        `Дуусах огноо: ${meta.expDate || "-"}`,
        `Төлөгдсөн огноо: ${meta.paidDate || "-"}`
      ];

      return parts.join(" | ");
    },
    clearFilters() {
      this.filterRequestType = null;
      this.filterStatus = null;
      this.filterCreatedBy = null;
      this.filterKeyName = null;
      this.filterFieldName = null;
      this.customerSearch = "";
    },
    requestRowClassName({ row }: { row: RequestRow }): string {
      switch (row.status) {
        case "PENDING":
          return "request-row-pending";
        case "CHECKED":
          return "request-row-checked";
        case "APPROVED":
          return "request-row-approved";
        case "REJECTED":
          return "request-row-rejected";
        default:
          return "";
      }
    },
    isNavigableRequest(row: RequestRow): boolean {
      return [
        "T_LOAN",
        "T_LOANLINE",
        "T_CUSTOMER",
        "T_CUSTOMER_DETAILS"
      ].includes(String(row.refType || "").toUpperCase());
    },
    openRequestTarget(row: RequestRow) {
      const refType = String(row.refType || "").toUpperCase();

      if (refType === "T_LOAN" || refType === "T_LOANLINE") {
        const detailType = refType === "T_LOANLINE" ? "loanline" : "loan";
        const orgCode =
          (row as any).orgCode ||
          (row as any).orgcode ||
          (row as any).providerRegnum ||
          store.getters.orgCode ||
          "";
        const id = (this as any).b64EncodeUnicode(
          `${row.keyValue}@${orgCode}@${row.identityNo ||
            row.customerId}@${detailType}`
        );
        this.$router.push({ name: "refloandetails", params: { id } });
        return;
      }

      if (refType === "T_CUSTOMER" || refType === "T_CUSTOMER_DETAILS") {
        const id = (this as any).b64EncodeUnicode(
          row.identityNo || row.customerId || row.keyValue
        );
        this.$router.push({ name: "refcustomerdetails", params: { id } });
      }
    },
    handleCurrentChange(val) {
      this.page = val;
    },
    handleChange(value) {
      this.pageSize = value;
      this.resetPagination();
    },
    resetPagination() {
      this.page = 1;
    },

    formatCurrency(value: number | string) {
      if (value === null || value === undefined || value === "") return "";

      const num = Number(value);

      if (isNaN(num)) return "";

      // Change minimumFractionDigits from 0 to 2

      return num.toLocaleString("en-US", {
        minimumFractionDigits: 2,

        maximumFractionDigits: 2 // Also good to set max for consistency
      });
    },

    parseCurrency(value: string) {
      // This function looks correct for parsing

      return value.replace(/[^\d.-]/g, "");
    },
    openRequestDetail(row: RequestRow) {
      this.selectedRequest = row;
      this.showRequestDetailModal = true;
    },
    canRemindRequest(row?: RequestRow | null): boolean {
      return !!row && ["PENDING", "CHECKED"].includes(row.status);
    },
    canWithdrawRequest(row?: RequestRow | null): boolean {
      return (
        !!row &&
        row.status === "PENDING" &&
        !!row.createdBy &&
        row.createdBy === this.currentUsername
      );
    },
    async remindRequest(row: RequestRow) {
      if (!row) return;
      this.reminding = true;
      try {
        const resp = await ApiService.put("updaterequest", {
          params: { type: "remind", id: row.id }
        });
        const rs = resp.data;
        if (rs.code !== 1) {
          ElNotification({
            title: "Алдаа",
            message: rs.error || "Сануулга илгээж чадсангүй.",
            type: "error"
          });
          return;
        }
        ElNotification({
          title: "Амжилттай",
          message: rs.response || "Сануулга илгээгдлээ.",
          type: "success"
        });
      } catch (e) {
        ElNotification({
          title: "Алдаа",
          message: "Сануулга илгээхэд алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.reminding = false;
      }
    },
    async withdrawRequest(row: RequestRow) {
      if (!row) return;
      const { isConfirmed } = await Swal.fire({
        title: "Хүсэлтийг буцааж устгах уу?",
        showCancelButton: true,
        confirmButtonText: "Тийм",
        cancelButtonText: "Үгүй"
      });
      if (!isConfirmed) return;

      this.withdrawing = true;
      try {
        const resp = await ApiService.put("updaterequest", {
          params: { type: "withdraw", id: row.id }
        });
        const rs = resp.data;
        if (rs.code !== 1) {
          ElNotification({
            title: "Алдаа",
            message: rs.error || "Хүсэлтийг буцааж чадсангүй.",
            type: "error"
          });
          return;
        }
        ElNotification({
          title: "Амжилттай",
          message: rs.response || "Хүсэлт буцаагдлаа.",
          type: "success"
        });
        this.showRequestDetailModal = false;
        this.getRequests();
      } catch (e) {
        ElNotification({
          title: "Алдаа",
          message: "Хүсэлтийг буцаахад алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.withdrawing = false;
      }
    },
    async sendOnce(type: "revise" | "cancel" | "approve" | "deny", id: number) {
      if (this.tableData.length === 0) {
        ElNotification({
          title: "Анхааруулга",
          message: "Хүсэлт байхгүй байна.",
          type: "warning"
        });
        return;
      }
      const { isConfirmed } = await Swal.fire({
        title: "Үйлдлийг зөвшөөрөх үү?",
        showCancelButton: true,
        confirmButtonText: "Тийм",
        cancelButtonText: "Үгүй"
      });
      if (!isConfirmed) return;

      this.loadingtable = true;
      try {
        const resp = await ApiService.put("updaterequest", {
          params: { type, id }
        });
        const rs = resp.data;
        if (rs.code !== 1) {
          ElNotification({
            title: "Алдаа",
            message: rs.error || "Амжилтгүй.",
            type: "error"
          });
          return;
        }
        ElNotification({
          title: "Амжилттай",
          message: rs.response,
          type: "success"
        });
        this.getRequests();
      } catch (e) {
        ElNotification({
          title: "Алдаа",
          message: "Алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.loadingtable = false;
      }
    },
    async sendAll(type: "reviseAll" | "cancelAll" | "approveAll" | "denyAll") {
      if (this.tableData.length === 0) {
        ElNotification({
          title: "Анхааруулга",
          message: "Хүсэлт байхгүй байна.",
          type: "warning"
        });
        return;
      }
      const { isConfirmed } = await Swal.fire({
        title: "Үйлдлийг зөвшөөрөх үү?",
        showCancelButton: true,
        confirmButtonText: "Тийм",
        cancelButtonText: "Үгүй"
      });
      if (!isConfirmed) return;

      this.loadingtable = true;
      try {
        // collect eligible IDs based on type
        const ids =
          type === "reviseAll" || type === "cancelAll"
            ? this.tableData.filter(r => r.status === "PENDING").map(r => r.id)
            : this.tableData.filter(r => r.status === "CHECKED").map(r => r.id);

        const resp = await ApiService.putWithRawBody(
          "updaterequest",
          { data: ids },
          { params: { type } }
        );
        const rs = resp.data;

        if (rs.code !== 1) {
          ElNotification({
            title: "Алдаа",
            message: rs.error || "Амжилтгүй.",
            type: "error"
          });
          return;
        }

        // Show per-row results
        const html = (rs.response || [])
          .map((x: any) => `<li><b>${x.reqid}</b> - ${x.result}</li>`)
          .join("");
        ElNotification({
          title: "Амжилттай",
          dangerouslyUseHTMLString: true,
          message: `<ol>${html}</ol>`,
          type: "success"
        });

        this.getRequests();
      } catch (e) {
        ElNotification({
          title: "Алдаа",
          message: "Алдаа гарлаа.",
          type: "error"
        });
      } finally {
        this.loadingtable = false;
      }
    },
    async getRequests() {
      this.loadingtable = true;
      await ApiService.get(`updaterequest`)
        .then(resp => {
          const results = resp.data;
          if (results.code === 0) {
            ElNotification({
              title: "Алдаа",
              message: results.error,
              type: "error",
              iconClass: "el-text-error"
            });
          } else if (results.code === 401) {
            ElNotification({
              title: "Анхааруулга",
              message: results.warning,
              type: "warning",
              iconClass: "el-text-warning"
            });
          } else {
            this.tableData = results.response.map((it: any) => ({
              id: it.id,
              refType: it.refType,
              refId: it.refId,
              requestType: it.requestType,
              customerId: it.customerId,
              identityNo: it.identityNo,
              customerName: it.customerName,
              keyName: it.keyName,
              keyValue: it.keyValue,
              fieldName: it.fieldName,
              oldValue: it.oldValue,
              newValue: it.newValue,
              oldValueString: it.oldValueString,
              newValueString: it.newValueString,
              requestReason: it.requestReason,
              status: it.status,
              statusText: it.statusText,
              createdBy: it.createdBy,
              createdDate: it.createdDate,
              checkedBy: it.checkedBy,
              checkedDate: it.checkedDate,
              approvedBy: it.approvedBy,
              approvedDate: it.approvedDate,
              pcrMailStatus: it.pcrMailStatus,
              pcrMailStatusText: it.pcrMailStatusText,
              pcrMailSentDate: it.pcrMailSentDate,
              pcrMailLastAttemptDate: it.pcrMailLastAttemptDate,
              pcrMailLastError: it.pcrMailLastError,
              canResendPcrMail: it.canResendPcrMail,
              searchfield: it.searchfield
            }));
          }
        })
        .catch(() => {
          ElNotification({
            title: "Алдаа",
            message: "Өгөгдөл уншихад алдаа гарлаа.",
            type: "error",
            iconClass: "el-text-error"
          });
        })
        .finally(() => {
          this.loadingtable = false;
        });
    },
    // === NEW ===
    openAddModal() {
      this.resetAddForm();
      this.showAddModal = true;
      this.metaError = "";
    },
    resetAddForm() {
      this.addForm = {
        actionType: "UPDATE",
        isCitizen: 1,
        customerId: "",
        identityNo: "",
        customerName: "",
        refId: "",
        modules: [],
        selectedPath: [],
        fields: [],
        selectedField: "",
        oldValue: "",
        newValue: "",
        reason: ""
      };
    },
    async fetchCustomerMeta() {
      this.metaError = "";
      if (!this.addForm.customerId) {
        this.metaError = "Харилцагчийн ID талбарыг бөглөнө үү.";
        return;
      }
      this.loadingMeta = true;
      try {
        const qs = new URLSearchParams({
          isCitizen: String(this.addForm.isCitizen),
          customerId: this.addForm.customerId,
          actionName: this.addForm.actionType
        }).toString();

        const resp = await ApiService.get(`updaterequest`, `meta?${qs}`);
        const rs = resp.data;
        if (rs.code !== 200) {
          this.metaError = rs.error || rs.warning || "Мета мэдээлэл олдсонгүй.";
          this.addForm.modules = [];
          this.addForm.customerName = "";
          this.addForm.identityNo = "";
          this.addForm.refId = "";
          return;
        }
        this.addForm.customerName = rs.response?.customerName || "";
        this.addForm.identityNo = rs.response?.identityNo || "";
        this.addForm.refId = rs.response?.refId || "";
        this.addForm.modules = rs.response?.modules || [];
        this.addForm.selectedPath = []; // reset selection
        this.addForm.oldValue = "";
        this.addForm.newValue = "";
      } catch (e) {
        this.metaError = "Мета мэдээлэл авахад алдаа гарлаа.";
      } finally {
        this.loadingMeta = false;
      }
    },

    onCascaderChange() {
      // Wait for the next DOM update cycle to ensure v-model is updated
      this.$nextTick(() => {
        const meta = this.selectedFieldMeta;

        if (this.addForm.actionType === "DELETE") {
          // No field/new value for delete
          this.addForm.oldValue = "";
          this.addForm.newValue = "";
          return;
        }

        // UPDATE – This will now access the updated 'selectedFieldMeta'
        this.addForm.oldValue = meta?.oldValue || "";
        this.addForm.newValue = ""; // Also reset newValue when the field changes
      });
    },
    async submitAdd() {
      const form = this.$refs.addFormRef as any;
      form.validate(async (valid: boolean) => {
        if (!valid) return;

        const meta = this.selectedFieldMeta;
        if (!meta) {
          ElNotification({
            title: "Анхааруулга",
            message: "Талбар сонгоно уу",
            type: "warning"
          });
          return;
        }

        const actionText =
          this.addForm.actionType === "UPDATE" ? "ЗАСАХ" : "УСТГАХ";
        const html = `<div style="text-align:left">
      <p><b>Төрөл:</b> ${this.addForm.isCitizen === 1 ? "Иргэн" : "ААН"}</p>
      <p><b>Харилцагч ID:</b> ${this.addForm.customerId}</p>
      <p><b>REF ID:</b> ${this.addForm.refId || "-"}</p>
      <p><b>Модуль:</b> ${meta.tableLabel}</p>
      <p><b>Мөр:</b> ${meta.keyLabel}</p>
      <p><b>Талбар:</b> ${meta.label} (${meta.table}.${meta.field})</p>
      <p><b>Анхны утга:</b> ${meta.oldValue || "-"}</p>
      ${
        this.addForm.actionType === "UPDATE"
          ? `<p><b>Шинэ утга:</b> ${this.addForm.newValue}</p>`
          : ""
      }
      <p><b>Шалтгаан:</b> ${this.addForm.reason}</p>
    </div>`;

        const ok = await Swal.fire({
          title: `${actionText} хүсэлт илгээх үү?`,
          html,
          showCancelButton: true,
          target: document.body,
          confirmButtonText: "Тийм",
          cancelButtonText: "Үгүй"
        });
        if (!ok.isConfirmed) return;

        this.submitting = true;
        try {
          const data = {
            action: this.addForm.actionType, // "UPDATE" | "DELETE"
            isCitizen: this.addForm.isCitizen, // 1|0
            identityNo: this.addForm.identityNo || this.addForm.customerId,
            customerName: this.addForm.customerName, // cross-check
            table: meta.table, // e.g. "T_LOAN"
            keyValue: meta.keyValue, // e.g. LOAN_CODE
            fieldName: meta.field, // e.g. "LOAN_CONTRACT_NO"
            oldValue: this.addForm.oldValue, // e.g. "LOAN_CONTRACT_NO"
            newValue:
              this.addForm.actionType === "UPDATE"
                ? this.addForm.newValue
                : null,
            reason: this.addForm.reason
          };

          const resp = await ApiService.postWithRawBody("updaterequest", {
            data
          });
          const rs = resp.data;

          if (rs.code !== 1) {
            ElNotification({
              title: "Алдаа",
              message: rs.error || rs.warning || "Амжилтгүй.",
              type: "error"
            });
          } else {
            ElNotification({
              title: "Амжилттай",
              message: "Хүсэлт амжилттай үүслээ.",
              type: "success"
            });
            this.showAddModal = false;
            this.getRequests();
          }
        } catch (e) {
          ElNotification({
            title: "Алдаа",
            message: "Хүсэлт илгээхэд алдаа гарлаа.",
            type: "error"
          });
        } finally {
          this.submitting = false;
        }
      });
    }
  }
});
